import React, { useEffect, useState } from 'react';
import { Drawer } from 'antd';
import {
  ShareAltOutlined, WalletOutlined, CloudSyncOutlined, PoweroffOutlined, RightOutlined, BarChartOutlined, SwapOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import axios from 'axios';
import config from '../../../config';
import usePermissions from '../../../hooks/usePermissions';
import { ecrireCasquette } from '../../../hooks/useCasquette';
import './livreurNav.css';

/**
 * Tiroir « Plus » du livreur (Phase A refonte navigation). Le livreur n'a pas de
 * sidebar : ce tiroir regroupe les actions secondaires jadis entassées dans la
 * barre du haut (catalogue de partage, dépenses, version/à-propos, déconnexion).
 *
 * Ouvert par l'événement window `ndoe:livreur-menu` (émis par le bouton menu de
 * la navbar ET par l'onglet « Plus » de la barre basse — une seule source de
 * vérité, comme `ndoe:apropos` pour la modale À propos qu'on réutilise ici).
 * Aucune logique métier : navigation + déconnexion (déplacée telle quelle depuis
 * la navbar) + relais vers AProposApp.
 */
export default function LivreurDrawer() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { can, profilMixte } = usePermissions();
  const currentUser = useSelector((s) => s.user.currentUser);
  const domain = config.REACT_APP_SERVER_DOMAIN;
  const username = currentUser?.username || 'Livreur';

  useEffect(() => {
    const h = () => setOpen(true);
    window.addEventListener('ndoe:livreur-menu', h);
    return () => window.removeEventListener('ndoe:livreur-menu', h);
  }, []);

  const fermer = () => setOpen(false);
  const aller = (route) => { fermer(); navigate(route); };

  const logout = async () => {
    fermer();
    try {
      await axios.post(`${domain}/api/auth/logout`);
      Swal.fire('Déconnexion réussie !', '', 'success');
      navigate('/login');
      window.location.reload();
    } catch (_) {
      Swal.fire('Erreur lors de la déconnexion.', '', 'error');
    }
  };

  // Une entrée = icône + libellé + chevron. Les entrées sans permission n'apparaissent pas.
  const Entree = ({ Icon, label, onClick, danger }) => (
    <button type="button" className={`livdrawer-item${danger ? ' is-danger' : ''}`} onClick={onClick}>
      <span className="livdrawer-item__ic"><Icon /></span>
      <span className="livdrawer-item__t">{label}</span>
      <RightOutlined className="livdrawer-item__chev" />
    </button>
  );

  return (
    <Drawer
      className="livdrawer"
      aria-label="Menu livreur"
      placement="right"
      width={Math.min(360, typeof window !== 'undefined' ? Math.round(window.innerWidth * 0.86) : 360)}
      open={open}
      onClose={fermer}
      closable={false}
      title={null}
      styles={{ body: { padding: 0 } }}
    >
      <div className="livdrawer-head">
        <span className="livdrawer-ava" aria-hidden="true">{username.charAt(0).toUpperCase()}</span>
        <span className="livdrawer-head__t"><b>{username}</b><small>Livreur</small></span>
      </div>
      <nav className="livdrawer-list" aria-label="Menu livreur">
        {/* PROFIL MIXTE : n'apparaît que si l'admin a accordé le privilège.
            La bascule change les écrans, pas les droits (le serveur décide). */}
        {profilMixte && (
          <Entree
            Icon={SwapOutlined}
            label="Passer en mode vendeur"
            onClick={() => { fermer(); ecrireCasquette('vendeur'); }}
          />
        )}
        <Entree Icon={BarChartOutlined} label="Mon activité du jour" onClick={() => aller('/mon-activite')} />
        {can('partage.voir') && (
          <Entree Icon={ShareAltOutlined} label="Catalogue de partage" onClick={() => aller('/partage-catalogue')} />
        )}
        {can('depenses.declarer') && (
          <Entree Icon={WalletOutlined} label="Mes dépenses du jour" onClick={() => aller('/ma-feuille-depense')} />
        )}
        <Entree Icon={CloudSyncOutlined} label="Version & mise à jour" onClick={() => { fermer(); window.dispatchEvent(new Event('ndoe:apropos')); }} />
        <Entree Icon={PoweroffOutlined} label="Déconnexion" onClick={logout} danger />
      </nav>
    </Drawer>
  );
}
